import { useCallback, useEffect, useRef, useState } from 'react'

const API_URL = import.meta.env.VITE_API_URL || '/v1'

/** How long a burst of changes (dragging a column edge) is coalesced before saving. */
const SAVE_DEBOUNCE_MS = 600

/**
 * A per-user UI preference stored on the server, so it follows the login
 * across browsers and devices.
 *
 * `loaded` turns true once the server answered (with or without a value);
 * until then callers should keep their built-in default. Failures are
 * swallowed — a preference that cannot be saved must never break the page.
 */
export function useUserPreference<T>(key: string) {
  const [value, setValue] = useState<T | null>(null)
  const [loaded, setLoaded] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pending = useRef<T | null>(null)
  const url = `${API_URL}/me/preferences/${encodeURIComponent(key)}`

  useEffect(() => {
    let cancelled = false
    fetch(url, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { value: null }))
      .then((body: { value: T | null }) => {
        if (!cancelled) setValue(body.value ?? null)
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [url])

  const flush = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    if (pending.current === null) return
    const body = JSON.stringify({ value: pending.current })
    pending.current = null
    void fetch(url, {
      method: 'PUT',
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body,
    }).catch(() => undefined)
  }, [url])

  // Don't lose the last change when the page is left mid-debounce
  useEffect(() => flush, [flush])

  const save = useCallback(
    (next: T) => {
      setValue(next)
      pending.current = next
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(flush, SAVE_DEBOUNCE_MS)
    },
    [flush],
  )

  return { value, loaded, save }
}
